import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  TrendingUp, TrendingDown, Minus, Download, ArrowUp, ArrowDown,
  FileText, Star,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import SchedulerControls from '../components/admin/SchedulerControls';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#002FA7', '#2A9D8F', '#E76F51', '#E9C46A', '#264653', '#F4A261', '#606c38', '#bc6c25'];

const xTick = { fontSize: 11, fill: '#999' };
const yTick = { fontSize: 11, fill: '#999' };
const YAXIS_LABEL = { value: 'Position', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#999' } };
const LINE_DOT = { r: 4 };

export default function KeywordEvolution() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [months, setMonths] = useState(3);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('change');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedKws, setSelectedKws] = useState([]);

  // Derive provider label from the selected client's `seo_provider` field
  // (returned by GET /api/clients). Falls back to "SEO" if no client is
  // selected or if the field is missing (legacy clients).
  const providerLabel = useMemo(() => {
    const selected = clients.find(c => c.id === selectedClientId);
    const raw = (selected?.seo_provider || '').toLowerCase();
    const map = {
      dataforseo: 'DataForSEO',
      haloscan: 'Haloscan',
      semrush: 'SEMrush',
      ubersuggest: 'Ubersuggest',
    };
    return map[raw] || 'SEO';
  }, [clients, selectedClientId]);

  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(r => setClients(r.data)).catch(() => {});
  }, []);

  const fetchEvolution = async (clientId, m, rangeStart, rangeEnd) => {
    if (!clientId) return;
    setLoading(true);
    try {
      let url = `${API}/keywords/client/${clientId}/evolution`;
      if (rangeStart && rangeEnd) {
        url += `?start=${rangeStart}&end=${rangeEnd}`;
      } else {
        url += `?months=${m}`;
      }
      const { data: d } = await axios.get(url, { withCredentials: true });
      setData(d);
      // Auto-select top 5 keywords for chart
      const top5 = d.keywords
        .filter(k => k.monthly_positions.some(p => p !== null))
        .slice(0, 5)
        .map(k => k.keyword_id);
      setSelectedKws(top5);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (id) => {
    setSelectedClientId(id);
    if (usingCustom && customStart && customEnd) {
      fetchEvolution(id, months, customStart, customEnd);
    } else {
      fetchEvolution(id, months);
    }
  };

  const handleMonthsChange = (m) => {
    setMonths(m);
    setUsingCustom(false);
    if (selectedClientId) fetchEvolution(selectedClientId, m);
  };

  const handleApplyCustom = () => {
    if (!customStart || !customEnd) return;
    setUsingCustom(true);
    if (selectedClientId) fetchEvolution(selectedClientId, months, customStart, customEnd);
  };

  const handleExport = () => {
    const qs = usingCustom && customStart && customEnd
      ? `start=${customStart}&end=${customEnd}`
      : `months=${months}`;
    window.open(
      `${API}/keywords/client/${selectedClientId}/evolution/export?${qs}&format=csv`,
      '_blank'
    );
  };

  const toggleKwChart = (kwId) => {
    setSelectedKws(prev =>
      prev.includes(kwId) ? prev.filter(id => id !== kwId) : [...prev, kwId]
    );
  };

  // Sort keywords
  const sortedKeywords = useMemo(() => {
    if (!data?.keywords) return [];
    return [...data.keywords].sort((a, b) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'keyword') return a.keyword.localeCompare(b.keyword) * dir;
      if (sortField === 'change') return ((a.change || 0) - (b.change || 0)) * dir;
      if (sortField === 'current') return ((a.current_position || 999) - (b.current_position || 999)) * dir;
      if (sortField === 'volume') return ((a.search_volume || 0) - (b.search_volume || 0)) * dir;
      return 0;
    });
  }, [data, sortField, sortDir]);

  // Build chart data
  const chartData = useMemo(() => {
    if (!data?.months_labels || !data?.keywords) return [];
    return data.months_labels.map((label, idx) => {
      const point = { month: label };
      data.keywords
        .filter(k => selectedKws.includes(k.keyword_id))
        .forEach(k => {
          point[k.keyword] = k.monthly_positions[idx];
        });
      return point;
    });
  }, [data, selectedKws]);

  const chartKeywords = useMemo(() => {
    if (!data?.keywords) return [];
    return data.keywords.filter(k => selectedKws.includes(k.keyword_id));
  }, [data, selectedKws]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const ChangeIndicator = ({ change }) => {
    if (change === null || change === undefined) return <span className="text-neutral-400">—</span>;
    if (change > 0) return <span className="text-emerald-600 font-medium flex items-center gap-1"><TrendingUp className="w-3 h-3" />+{change}</span>;
    if (change < 0) return <span className="text-red-500 font-medium flex items-center gap-1"><TrendingDown className="w-3 h-3" />{change}</span>;
    return <span className="text-neutral-400 flex items-center gap-1"><Minus className="w-3 h-3" />0</span>;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="kw-evolution-page">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
            Évolution des mots-clés
          </h1>
          <p className="text-neutral-500 mt-1" data-testid="kw-evolution-subtitle">
            Suivi de position {providerLabel} + Search Console
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
          <div className="flex-1 max-w-xs">
            <Select value={selectedClientId} onValueChange={handleClientChange}>
              <SelectTrigger data-testid="evo-client-select">
                <SelectValue placeholder="Sélectionner un client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[3, 6, 12].map(m => (
              <Button
                key={m} size="sm"
                variant={!usingCustom && months === m ? 'default' : 'outline'}
                className={!usingCustom && months === m ? 'bg-[#002FA7] hover:bg-[#002277]' : ''}
                onClick={() => handleMonthsChange(m)}
                data-testid={`evo-months-${m}`}
              >
                {m} mois
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 border-l pl-4 border-neutral-200">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="text-sm border border-neutral-200 rounded px-2 py-1"
              data-testid="evo-custom-start"
            />
            <span className="text-neutral-400 text-sm">→</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="text-sm border border-neutral-200 rounded px-2 py-1"
              data-testid="evo-custom-end"
            />
            <Button
              size="sm"
              variant={usingCustom ? 'default' : 'outline'}
              className={usingCustom ? 'bg-[#002FA7] hover:bg-[#002277]' : ''}
              onClick={handleApplyCustom}
              disabled={!customStart || !customEnd}
              data-testid="evo-custom-apply"
            >
              Appliquer
            </Button>
          </div>
          {data && (
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="evo-export-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter CSV
            </Button>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
          </div>
        )}

        {/* No data */}
        {!selectedClientId && !loading && (
          <div className="bg-white border border-neutral-200 p-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Sélectionnez un client pour voir l'évolution de ses mots-clés</p>
          </div>
        )}

        {data && !loading && (
          <>
            {/* Info banner if history is thin */}
            {data.total_history_entries !== undefined && data.total_history_entries < 3 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-4 text-sm text-blue-800" data-testid="evo-history-info">
                <p className="font-medium mb-1">Historique limité ({data.total_history_entries} relevé{data.total_history_entries > 1 ? 's' : ''})</p>
                <p className="text-blue-700">
                  Un tracking automatique se lance <strong>chaque lundi à 04h00 UTC</strong> et actualise tous les mots-clés de tous les clients — l'historique se remplit donc tout seul, sans action manuelle.
                  Vous pouvez aussi forcer une exécution immédiate via le bouton ci-dessous, ou cliquer "Actualiser tous" sur la page Mots-clés.
                  Les données Search Console ci-dessous remontent en plus les 30 derniers jours.
                </p>
                <div className="mt-3">
                  <SchedulerControls />
                </div>
              </div>
            )}

            {/* Chart */}
            {chartData.length > 0 && chartKeywords.length > 0 && (
              <div className="bg-white border border-neutral-200 p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
                  Évolution des positions — {data.client_name}
                </h2>
                <p className="text-xs text-neutral-400 mb-4">
                  Cliquez sur un mot-clé dans le tableau pour l'ajouter/retirer du graphique
                </p>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={xTick} />
                    <YAxis reversed tick={yTick} domain={['dataMin - 2', 'dataMax + 2']} label={YAXIS_LABEL} />
                    <Tooltip />
                    {chartKeywords.map((k, idx) => (
                      <Line
                        key={k.keyword_id}
                        type="monotone"
                        dataKey={k.keyword}
                        stroke={COLORS[idx % COLORS.length]}
                        strokeWidth={2}
                        dot={LINE_DOT}
                        connectNulls
                        name={k.keyword}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-4 justify-center flex-wrap mt-3">
                  {chartKeywords.map((k, idx) => (
                    <div key={k.keyword_id} className="flex items-center gap-1.5 text-xs">
                      <div className="w-3 h-3 rounded-full" style={{ background: COLORS[idx % COLORS.length] }} />
                      {k.keyword}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DataForSEO Keywords Table */}
            <div className="bg-white border border-neutral-200 p-5">
              <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
                Mots-clés suivis ({data.keywords.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 text-left text-neutral-500">
                      <th className="pb-2 pr-2 w-8"></th>
                      <th className="pb-2 pr-4 cursor-pointer" onClick={() => toggleSort('keyword')}>
                        Mot-clé {sortField === 'keyword' && (sortDir === 'desc' ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />)}
                      </th>
                      <th className="pb-2 px-2 text-right cursor-pointer" onClick={() => toggleSort('volume')}>Vol.</th>
                      {data.months_labels.map(label => (
                        <th key={label} className="pb-2 px-2 text-right font-normal text-xs">{label}</th>
                      ))}
                      <th className="pb-2 px-2 text-right cursor-pointer" onClick={() => toggleSort('current')}>Actuelle</th>
                      <th className="pb-2 px-2 text-right cursor-pointer" onClick={() => toggleSort('change')}>
                        Évol. {sortField === 'change' && (sortDir === 'desc' ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />)}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedKeywords.map(kw => {
                      const isSelected = selectedKws.includes(kw.keyword_id);
                      return (
                        <tr
                          key={kw.keyword_id}
                          className={`border-b border-neutral-50 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-neutral-50'}`}
                          onClick={() => toggleKwChart(kw.keyword_id)}
                        >
                          <td className="py-2 pr-2">
                            {kw.is_priority && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                          </td>
                          <td className="py-2 pr-4 text-neutral-800 font-medium max-w-[200px] truncate" title={kw.keyword}>
                            {kw.keyword}
                          </td>
                          <td className="py-2 px-2 text-right font-mono text-xs text-neutral-500">
                            {kw.search_volume || '—'}
                          </td>
                          {kw.monthly_positions.map((pos, idx) => (
                            <td key={`${kw.keyword_id}-m${idx}`} className="py-2 px-2 text-right font-mono text-sm">
                              {pos !== null ? (
                                <span className={pos <= 3 ? 'text-emerald-600 font-bold' : pos <= 10 ? 'text-[#002FA7]' : 'text-neutral-600'}>
                                  {pos}
                                </span>
                              ) : <span className="text-neutral-300">—</span>}
                            </td>
                          ))}
                          <td className="py-2 px-2 text-right">
                            {kw.current_position ? (
                              <span className={`font-mono font-bold ${kw.current_position <= 3 ? 'text-emerald-600' : kw.current_position <= 10 ? 'text-[#002FA7]' : 'text-neutral-700'}`}>
                                {kw.current_position}
                              </span>
                            ) : <span className="text-neutral-300">—</span>}
                          </td>
                          <td className="py-2 px-2 text-right">
                            <ChangeIndicator change={kw.change} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Search Console Keywords */}
            {data.gsc_keywords && data.gsc_keywords.length > 0 && (
              <div className="bg-white border border-neutral-200 p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-1">
                  Données Search Console ({data.gsc_keywords.length} mots-clés)
                </h2>
                <p className="text-xs text-neutral-400 mb-4">Clics, impressions et CTR des 30 derniers jours</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-200 text-left text-neutral-500">
                        <th className="pb-2 pr-4">Mot-clé</th>
                        <th className="pb-2 px-2 text-right">Clics</th>
                        <th className="pb-2 px-2 text-right">Impressions</th>
                        <th className="pb-2 px-2 text-right">CTR</th>
                        <th className="pb-2 px-2 text-right">Position</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.gsc_keywords.slice(0, 30).map(kw => (
                        <tr key={kw.keyword} className="border-b border-neutral-50 hover:bg-neutral-50">
                          <td className="py-2 pr-4 text-neutral-800 max-w-[250px] truncate">{kw.keyword}</td>
                          <td className="py-2 px-2 text-right font-mono font-medium text-[#002FA7]">{kw.clicks}</td>
                          <td className="py-2 px-2 text-right font-mono text-neutral-600">{kw.impressions.toLocaleString('fr-FR')}</td>
                          <td className="py-2 px-2 text-right font-mono text-[#2A9D8F]">{kw.ctr}%</td>
                          <td className="py-2 px-2 text-right font-mono">{kw.position}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
